import React, { useRef, useEffect } from 'react';
import { Filter } from 'lucide-react';

/**
 * MultiSelectFilter - Reusable multi-select dropdown filter component
 * 
 * @param {string} label - Filter button label
 * @param {Set} selectedItems - Set of selected item values
 * @param {Function} onToggle - Callback when an item is toggled
 * @param {Array} groups - Array of option groups
 *   Each group: { label: string, options: Array<{value: string, label: string, badge?: string}> }
 * @param {string} testId - data-testid for testing
 */
const MultiSelectFilter = ({ 
  label, 
  selectedItems, 
  onToggle, 
  groups, 
  testId 
}) => {
  const dropdownRef = useRef(null);
  const dropdownId = `${testId}-dropdown`;

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const dropdown = document.getElementById(dropdownId);
        if (dropdown) dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownId]);

  const toggleDropdown = () => {
    const dropdown = document.getElementById(dropdownId);
    if (dropdown) dropdown.classList.toggle('hidden');
  };

  return (
    <div ref={dropdownRef}>
      <div className="relative">
        <button
          type="button"
          className="w-full px-4 py-2 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={toggleDropdown}
          data-testid={testId}
        >
          <span className="text-sm font-medium">
            {label} ({selectedItems.size})
          </span>
          <Filter className="h-4 w-4 text-gray-400" />
        </button>
        
        <div 
          id={dropdownId}
          className="hidden absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          <div className="p-2 space-y-1">
            {groups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {group.label && (
                  <div className="font-semibold text-xs text-gray-500 px-2 py-1 mt-2 first:mt-0">
                    {group.label}
                  </div>
                )}
                {group.options.map((option) => (
                  <label 
                    key={option.value} 
                    className="flex items-center px-2 py-1 hover:bg-gray-50 cursor-pointer rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.has(option.value)}
                      onChange={() => onToggle(option.value)}
                      className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm flex-1 capitalize">
                      {option.label || option.value.replace(/_/g, ' ')}
                    </span>
                    {option.badge && (
                      <span className="ml-auto text-xs text-gray-500">
                        {option.badge}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectFilter;
